'use client'

import { formatRemainingTime } from '@polaris/core/utils'
import { useAtomValue } from 'jotai'
import { useState } from 'react'
import { usePendingTrades } from '@/src/hooks/trade/use-pending-trades'
import { useInterval } from '@/src/hooks/use-interval'
import { swapRequirementsAtom } from '@/src/stores/swap/atoms'
import { RadialProgress } from '../radial-progress'
import { Spinner } from '../ui/spinner'

export const PendingTradeBanner = () => {
  const [now, setNow] = useState(Date.now())
  const { firstPendingRoute } = usePendingTrades()
  const { enabled: tradeButtonEnabled } = useAtomValue(swapRequirementsAtom)

  const timing = firstPendingRoute?.timingMs
  const startMs = timing?.startMs
  // Add 5s buffer to ensure timer doesn't run out before hotkey transfer completes
  // The route DurationInSeconds doesn't include the final hotkey-to-destination transfer
  const HOTKEY_TRANSFER_BUFFER_MS = 5 * 1000
  const endMs = timing?.endMs
    ? timing.endMs + HOTKEY_TRANSFER_BUFFER_MS
    : undefined

  // Update current time every second to recalculate remaining time
  useInterval(() => setNow(Date.now()), startMs && endMs ? 1000 : null)

  // Only show when BOTH conditions are met:
  // 1. There's a pending trade (blocks new trades)
  // 2. Trade button would be enabled (valid amount, route, etc.) - this replaces the button
  if (!firstPendingRoute || !startMs || !endMs || !tradeButtonEnabled) {
    return null
  }

  const remaining = Math.max(0, endMs - now)

  const isOvertime = remaining <= 0

  const mainText = isOvertime
    ? 'Resume trading in a moment'
    : `Resume trading in about ${formatRemainingTime(remaining)}`

  return (
    <div className='flex items-start self-stretch rounded-2xl border border-border2 p-4 gap-4 mb-4'>
      <div className='size-6'>
        {isOvertime ? (
          <Spinner className='text-border2' />
        ) : (
          <RadialProgress
            animated
            startTime={startMs}
            endTime={endMs}
            strokeWidth={1}
            circleClasses='text-border2'
            progressClasses='-scale-y-100 text-accent-primary'
          />
        )}
      </div>
      <div className='flex flex-1 flex-col gap-1'>
        <p className='self-stretch text-sm font-semibold leading-5 tracking-[0.07px] text-primary'>
          {mainText}
        </p>
        <p className='max-w-md self-stretch text-sm leading-5 tracking-[0.07px] text-secondary'>
          Wait for your previous trade to complete before starting a new trade.
        </p>
      </div>
    </div>
  )
}
